import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Task, Link } from "@dhx/react-gantt";
import dayjs from "dayjs";
import {
  useGetSchedules,
  useCreateSchedule,
  useUpdateSchedule,
  useUpdateMultipleSchedules,
  useDeleteSchedule,
  useCreateLink,
  useDeleteLink,
} from "../mutations";
import { useOutageWindowData } from "./useOutageWindowData";
import {
  PhaseFormData,
  ActivityFormData,
  StageGateFormData,
  ProjectGanttHandle,
  StageGateFormHandle,
  ActivityFormHandle,
  PhaseFormHandle,
} from "../components";
import {
  getStatusFromProgress,
  calculateEndDate,
  formatDateForAPI,
  transformSchedulingItemsToTasks,
} from "../utils/transformers";
import {
  CreateSchedulingItemPayload,
  UpdateSchedulingItemPayload,
  SchedulingItem,
  DependencyType,
} from "../types/scheduling";
import { useProjects } from "../../projects/hooks/useProjects";
import { useOperations } from "../../recurringTasks/hooks/useOperations";
import { useOperationsList } from "../../recurringTasks/hooks/useOperationsList";
import { useSearchParams } from "react-router-dom";
import { useFetchProjects } from "../../projects";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { formatApiEndDate, formatApiStartDate, formatDate } from "../../../shared/utils";
import { getActiveOutageWindows } from "../utils/outageWindowUtils";

type DrawerType = "phase" | "activity" | "stageGate" | "outageWindow";

const PHASE_FIELDS = {
  phase: false,
  owner: false,
  userId: false,
  plannedStartDate: false,
  plannedEndDate: false,
  actualEndDate: false,
  status: false,
  color: false,
  progress: false,
};

const ACTIVITY_FIELDS = {
  title: false,
  phase: false,
  category: false,
  plannedStartDate: false,
  plannedEndDate: false,
  actualEndDate: false,
  owner: false,
  progress: false,
  is_recurring: false,
  frequency: false,
  recurring_day: false,
  monthly_pattern: false,
  description: false,
  asset_id: false,
};

const STAGE_GATE_FIELDS = {
  title: false,
  description: false,
  owner: false,
  userId: false,
  plannedCompletionDate: false,
  actualCompletionDate: false,
  parentPhaseId: false,
  parentPhase: false,
};

const OUTAGE_WINDOW_FIELDS = {
  plannedStartDate: false,
  plannedEndDate: false
}

export const useScheduleEditor = (projectId: number) => {
  // ==================== SETUP & INITIALIZATION ====================
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const { data, isLoading, isError, error, refetch } = useGetSchedules(projectId);
  const { outageWindowsData: outageWindows, isLoading: isOutageLoading } = useOutageWindowData({ projectId: projectId.toString() });

  const createMutation = useCreateSchedule(projectId);
  const updateMutation = useUpdateSchedule(projectId);
  const updateMultipleMutation = useUpdateMultipleSchedules(projectId);
  const deleteMutation = useDeleteSchedule(projectId);

  const createLinkMutation = useCreateLink(projectId);
  const deleteLinkMutation = useDeleteLink(projectId);

  const { removeCacheProjects, removeCachestages } = useFetchProjects(false, false, undefined, { my_tasks: false });
  const { removeCachePinned, removeCacheUnpinned } = useProjects({ searchKey: '', portfolioId: undefined, isPinned: false, isArchived: false, quickFilters: {
    region: null, programme: null, site: null, workstream: null, "worktype-group": null,
    projectYear: null, stakeholder: null, status: null
  }, phase_name: null, phase_type_id: null, page: 1, limit: 10, my_tasks: false });

  const { removeCache: removeOperationsCache } = useOperations({ projectId: projectId.toString() });
  const { removeCache: removeOperationsListCache } = useOperationsList({ projectId: projectId.toString() });

  const {
    isAllowedToEditActivity, isAllowedToEditPhase, isAllowedToEditStageGate,
    isAllowedToOverridePhaseLock, isAllowedToOverrideActivityLock, isAllowedToOverrideStageGateLock,
    isAllowedToOverrideCommissionPhaseCompletion,
  } = useCapabilities();

  const schedules = useMemo(() => data?.data?.rows || [], [data?.data?.rows]);
  const links = useMemo(() => data?.data?.links || [], [data?.data?.links]);

  // ==================== STATE DECLARATIONS ====================
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState<string>("day");
  const [showSlack, setShowSlack] = useState(false);
  const [showCriticalPath, setShowCriticalPath] = useState(false);

  const [phaseDisabledConfig, setPhaseDisabledConfig] = useState({
    phase: false,
    owner: false,
    userId: false,
    plannedStartDate: false,
    plannedEndDate: false,
    actualEndDate: false,
    status: false,
    color: false,
    progress: false,
  });

  const [activityDisabledConfig, setActivityDisabledConfig] = useState({
    title: false,
    phase: false,
    category: false,
    plannedStartDate: false,
    plannedEndDate: false,
    actualEndDate: false,
    owner: false,
    progress: false,
    is_recurring: false,
    frequency: false,
    recurring_day: false,
    monthly_pattern: false,
    description: false,
    asset_id: false,
  });

  const [stageGateDisabledConfig, setStageGateDisabledConfig] = useState({
    title: false,
    description: false,
    owner: false,
    userId: false,
    plannedCompletionDate: false,
    actualCompletionDate: false,
    parentPhaseId: false,
    parentPhase: false,
  });

  const [outageWindowDisabledConfig, setOutageWindowDisabledConfig] = useState({
    plannedStartDate: false,
    plannedEndDate: false
  })

  // ==================== REFS ====================
  const phaseFormRef = useRef<PhaseFormHandle>(null);
  const activityFormRef = useRef<ActivityFormHandle>(null);
  const stageGateFormRef = useRef<StageGateFormHandle>(null);
  const ganttRef = useRef<ProjectGanttHandle>(null);

  // ==================== API MUTATION CALLBACKS ====================
  const createSchedule = useCallback(
    async (payload: CreateSchedulingItemPayload) => {
      try {
        const response = await createMutation.mutateAsync(payload);
        return response.data.row;
      } catch (error) {
        throw error;
      }
    },
    [createMutation]
  );

  const updateSchedule = useCallback(
    async (
      scheduleId: number,
      payload: Partial<UpdateSchedulingItemPayload>
    ) => {
      try {
        const response = await updateMutation.mutateAsync({
          scheduleId,
          payload,
        });
        return response.data.row;
      } catch (error) {
        throw error;
      }
    },
    [updateMutation]
  );

  const updateMultipleSchedules = useCallback(
    async (items: UpdateSchedulingItemPayload[]) => {
      try {
        return await updateMultipleMutation.mutateAsync({ items });
      } catch (error) {
        throw error;
      }
    },
    [updateMultipleMutation]
  );

  const deleteSchedule = useCallback(
    async (scheduleId: number) => {
      try {
        return await deleteMutation.mutateAsync(scheduleId);
      } catch (error) {
        throw error;
      }
    },
    [deleteMutation]
  );

  // ==================== GETTER FUNCTIONS ====================
  const getScheduleById = useCallback(
    (scheduleId: number): SchedulingItem | undefined => {
      return schedules.find((schedule) => schedule.id === scheduleId);
    },
    [schedules]
  );

  const getSchedulesByParentId = useCallback(
    (parentId: number | null): SchedulingItem[] => {
      return schedules.filter((schedule) => schedule.parent_id === parentId);
    },
    [schedules]
  );

  const getSchedulesByType = useCallback(
    (type: "phase" | "activity" | "stage_gate"): SchedulingItem[] => {
      return schedules.filter((schedule) => schedule.type === type);
    },
    [schedules]
  );

  // Mirrors the phase/activity/stage_gate field-lock rules used for the drawer forms
  // (phaseDisabledConfig/activityDisabledConfig/stageGateDisabledConfig), so dragging a
  // Gantt bar is gated by the exact same permission a user would have inside the form.
  const canEditScheduleItemViaDrag = useCallback(
    (item: SchedulingItem | undefined, mode: string): boolean => {
      if (!item) return false;

      if (item.type === "phase") {
        if (isAllowedToOverridePhaseLock) return true;

        const stageCompleted = schedules.some(
          (s) => s.type === "stage_gate" && s.parent_id === item.id && s.status === "completed"
        );
        if (stageCompleted) return false;

        return Boolean(
          (item.user_id && item.is_action_owner) ||
          (item.user_id == null && isAllowedToEditPhase)
        );
      }

      if (item.type === "activity") {
        if (isAllowedToOverrideActivityLock) return true;

        const stageCompleted = schedules.some(
          (s) => s.type === "stage_gate" && s.parent_id === item.parent_id && s.status === "completed"
        );
        if (stageCompleted) return false;

        if (item.user_id && item.is_action_owner) {
          // Owner branch only unlocks the progress field (mirrors activityDisabledConfig) —
          // planned dates stay locked, so move/resize must stay blocked.
          return mode === "progress";
        }

        return Boolean(item.user_id == null && isAllowedToEditActivity);
      }

      if (item.type === "stage_gate") {
        if (isAllowedToOverrideStageGateLock) return true;
        if (item.status === "completed") return false;
        if (item.user_id && item.is_action_owner) return false; // ownership locks stage gate fields (existing asymmetry)

        return Boolean(item.user_id == null && isAllowedToEditStageGate);
      }

      return false;
    },
    [
      schedules,
      isAllowedToEditPhase,
      isAllowedToEditActivity,
      isAllowedToEditStageGate,
      isAllowedToOverridePhaseLock,
      isAllowedToOverrideActivityLock,
      isAllowedToOverrideStageGateLock,
    ]
  );

  const canEditTaskViaDrag = useCallback(
    (taskId: number, mode: string): boolean => canEditScheduleItemViaDrag(getScheduleById(taskId), mode),
    [canEditScheduleItemViaDrag, getScheduleById]
  );

  // Generalized (arbitrary-phase) versions of the areAllActivitiesCompleted /
  // hasLinkedStageGate rules used for the Phase form's "Completed" status gate,
  // so the same business rule can be enforced when completing a phase via drag.
  const getAreAllActivitiesCompletedForPhase = useCallback(
    (phaseId: number): boolean => {
      const phaseActivities = schedules.filter((s) => s.type === "activity" && s.parent_id === phaseId);
      if (phaseActivities.length === 0) return true;
      return phaseActivities.every((activity) => activity.status === "completed");
    },
    [schedules]
  );

  const getLinkedStageGateForPhase = useCallback(
    (phaseId: number): { hasLinkedStageGate: boolean; isCompleted: boolean } => {
      const hasLinked = links.some((link) => {
        const sourceId = Number(link.source_id);
        const targetId = Number(link.target_id);
        const isPhaseSourceStageGateTarget = sourceId === phaseId && schedules.some((s) => s.id === targetId && s.type === "stage_gate");
        const isStageGateSourcePhaseTarget = targetId === phaseId && schedules.some((s) => s.id === sourceId && s.type === "stage_gate");
        return isPhaseSourceStageGateTarget || isStageGateSourcePhaseTarget;
      });

      if (!hasLinked) return { hasLinkedStageGate: false, isCompleted: false };

      const linkedStageGate = schedules.find((s) => {
        if (s.type !== "stage_gate") return false;
        return links.some((link) => {
          const sourceId = Number(link.source_id);
          const targetId = Number(link.target_id);
          const isPhaseSourceStageGateTarget = sourceId === phaseId && targetId === s.id;
          const isStageGateSourcePhaseTarget = targetId === phaseId && sourceId === s.id;
          return isPhaseSourceStageGateTarget || isStageGateSourcePhaseTarget;
        });
      });

      return { hasLinkedStageGate: true, isCompleted: linkedStageGate?.status === "completed" };
    },
    [schedules, links]
  );

  // Mirrors PhaseForm's validateCommissioningPhaseAgainstOutageWindows — blocks (no
  // override, for anyone) moving/resizing a Commissioning Phase past the active
  // Outage Window's end date.
  const validatePhaseDatesAgainstOutageWindow = useCallback(
    (item: SchedulingItem, newPlannedEndDateISO: string): { allowed: boolean; message?: string } => {
      const isCommissioningPhase = item.name?.toLowerCase().includes("commissioning");
      if (!isCommissioningPhase || !outageWindows || outageWindows.length === 0) {
        return { allowed: true };
      }

      const activeOutageWindows = getActiveOutageWindows(outageWindows);
      if (activeOutageWindows.length === 0) {
        return { allowed: true };
      }

      const maxActiveOutageWindow = activeOutageWindows.reduce((max, current) => {
        const maxEndDate = dayjs(max.outage_end_date).startOf("day");
        const currentEndDate = dayjs(current.outage_end_date).startOf("day");
        return currentEndDate.isAfter(maxEndDate) ? current : max;
      });
      const outageEndDate = dayjs(maxActiveOutageWindow.outage_end_date).startOf("day");
      const plannedEndDate = dayjs(newPlannedEndDateISO).startOf("day");

      if (plannedEndDate.isAfter(outageEndDate)) {
        return {
          allowed: false,
          message: `The Commissioning Phase's Planned End Date must be equal to or before the active Outage Window End Date (${formatDate(outageEndDate.toDate())}).`,
        };
      }

      return { allowed: true };
    },
    [outageWindows]
  );

  // Mirrors PhaseForm's Completed-status MenuItem gate (areAllActivitiesCompleted /
  // hasLinkedStageGate, no override for anyone) plus validateCommissioningPhaseCompletion
  // (outage-window completion check, overridable via isAllowedToOverrideCommissionPhaseCompletion).
  const validatePhaseCompletionBusinessRules = useCallback(
    (item: SchedulingItem): { allowed: boolean; message?: string } => {
      const allActivitiesCompleted = getAreAllActivitiesCompletedForPhase(item.id);
      const { hasLinkedStageGate: hasLinked, isCompleted: linkedCompleted } = getLinkedStageGateForPhase(item.id);

      if (!allActivitiesCompleted || (hasLinked && !linkedCompleted)) {
        return {
          allowed: false,
          message: `This phase cannot be marked Completed until all its activities are completed${hasLinked ? " and its linked stage gate is completed" : ""}.`,
        };
      }

      const isCommissioningPhase = item.name?.toLowerCase().includes("commissioning");
      if (!isCommissioningPhase || !outageWindows || outageWindows.length === 0) {
        return { allowed: true };
      }

      const today = dayjs().startOf("day");
      const activeOutageWindows = getActiveOutageWindows(outageWindows);

      if (activeOutageWindows.length > 0) {
        const maxActiveOutageWindow = activeOutageWindows.reduce((max, current) => {
          const maxEndDate = dayjs(max.outage_end_date).startOf("day");
          const currentEndDate = dayjs(current.outage_end_date).startOf("day");
          return currentEndDate.isAfter(maxEndDate) ? current : max;
        });
        const outageEndDate = dayjs(maxActiveOutageWindow.outage_end_date).startOf("day");

        if (today.isAfter(outageEndDate)) {
          if (isAllowedToOverrideCommissionPhaseCompletion) return { allowed: true };
          return {
            allowed: false,
            message: `The Actual End Date must be equal to or before the active Outage Window End Date (${formatDate(outageEndDate.toDate())}).`,
          };
        }

        return { allowed: true };
      }

      const expiredWindows = outageWindows.filter((w) => dayjs(w.outage_end_date).startOf("day").isBefore(today));
      const inactiveWindows = outageWindows.filter((w) => w.status === "in_active");

      if (expiredWindows.length > 0 || inactiveWindows.length > 0) {
        if (isAllowedToOverrideCommissionPhaseCompletion) return { allowed: true };
        return {
          allowed: false,
          message: "The Commissioning Phase can only be completed during an active Outage Window period.",
        };
      }

      return { allowed: true };
    },
    [getAreAllActivitiesCompletedForPhase, getLinkedStageGateForPhase, outageWindows, isAllowedToOverrideCommissionPhaseCompletion]
  );

  // ==================== MEMOIZED VALUES ====================
  const editingScheduleItem = useMemo(() => schedules.find((s) => s.id === Number(id)) || null, [schedules, id]);

  const phaseFormData = useMemo((): Partial<PhaseFormData> => {
    if (!editingScheduleItem) return {};
    return {
      phaseId: editingScheduleItem.id,
      phase: editingScheduleItem.name,
      phase_type_id: editingScheduleItem.phase_type_id || null,
      plannedStartDate: editingScheduleItem.planned_start_date
        ? editingScheduleItem.planned_start_date
        : null,
      plannedEndDate: editingScheduleItem.planned_end_date
        ? editingScheduleItem.planned_end_date
        : null,
      progress: parseFloat(editingScheduleItem.progress_percentage) || 0,
      owner: editingScheduleItem.owner || "",
      userId: editingScheduleItem.user_id || undefined,
      status:
        (editingScheduleItem.status as string) === "in_progress"
          ? "In Progress"
          : editingScheduleItem.status === "on_hold"
            ? "On Hold"
            : editingScheduleItem.status === "completed"
              ? "Completed"
              : "Planned",
      color: editingScheduleItem.color || "#2196F3",
      actualEndDate: editingScheduleItem.actual_end_date,
    };
  }, [editingScheduleItem]);

  const activityFormData = useMemo((): Partial<ActivityFormData> => {
    if (!editingScheduleItem) return {};
    const parentPhase = editingScheduleItem.parent_id
      ? getScheduleById(editingScheduleItem.parent_id)
      : null;
    return {
      title: editingScheduleItem.name,
      plannedStartDate: editingScheduleItem.planned_start_date
        ? dayjs(editingScheduleItem.planned_start_date).startOf('day')
        : dayjs().startOf('day'),
      plannedEndDate: editingScheduleItem.planned_end_date
        ? dayjs(editingScheduleItem.planned_end_date).startOf('day')
        : dayjs().startOf('day'),
      actualEndDate: editingScheduleItem.actual_end_date
        ? dayjs(editingScheduleItem.actual_end_date).startOf('day')
        : undefined,
      progress: parseFloat(editingScheduleItem.progress_percentage) || 0,
      owner: editingScheduleItem.owner || "",
      userId: editingScheduleItem.user_id || undefined,
      phase: parentPhase?.name || "",
      phaseId: editingScheduleItem.parent_id || undefined,
      category: editingScheduleItem.category_display_text || "",
      category_key: editingScheduleItem.category || undefined,
      is_recurring: editingScheduleItem.is_recurring || false,
      frequency: editingScheduleItem.frequency || "",
      description: editingScheduleItem.description || "",
      recurring_day: editingScheduleItem.recurring_day || [],
      asset_id: editingScheduleItem.asset_id || null,
      monthly_pattern: (editingScheduleItem.monthly_pattern as "day_of_month" | "day_of_week" | undefined) || undefined,
    };
  }, [editingScheduleItem, getScheduleById]);

  const stageGateFormData = useMemo((): Partial<StageGateFormData> => {
    if (!editingScheduleItem) return {};
    const parentPhase = editingScheduleItem.parent_id
      ? getScheduleById(editingScheduleItem.parent_id)
      : null;
    return {
      title: editingScheduleItem.name,
      plannedStartDate: editingScheduleItem.planned_start_date
        ? dayjs(editingScheduleItem.planned_start_date).startOf('day')
        : dayjs().startOf('day'),
      plannedEndDate: editingScheduleItem.planned_end_date
        ? dayjs(editingScheduleItem.planned_end_date).startOf('day')
        : dayjs().startOf('day'),
      actualEndDate: editingScheduleItem.actual_end_date
        ? dayjs(editingScheduleItem.actual_end_date).startOf('day')
        : undefined,
      owner: editingScheduleItem.owner || "",
      userId: editingScheduleItem.user_id || undefined,
      description: editingScheduleItem.description || "",
      parentPhase: parentPhase?.name || "",
      parentPhaseId: editingScheduleItem.parent_id || undefined,
      stage_gate_id: editingScheduleItem.stage_gate_id || undefined,
    };
  }, [editingScheduleItem, getScheduleById]);

  function sortTasksWithMilestoneLast(tasks: Task[]): Task[] {

    const projects = tasks.filter((task) => task.type === "project");
    const result: Task[] = [];
    
    projects.forEach((project) => {
      result.push(project);

      const children = tasks.filter((task) => Number(task.parent) === Number(project.id));

      const milestones = children.filter((child) => child.type === "milestone");
      const others = children.filter((child) => child.type !== "milestone");

      result.push(...others, ...milestones);
    })

    return result;
  }

  const tasks = useMemo(() => {
    if (!schedules.length) return [];
    const transformed =  transformSchedulingItemsToTasks(schedules);
    return sortTasksWithMilestoneLast(transformed);
  }, [schedules]);

  const ganttLinks: Link[] = useMemo(() => {
    const apiTypeToGanttType: Record<string, string> = {
      finish_to_start: "0",
      start_to_start: "1",
      finish_to_finish: "2",
      start_to_finish: "3",
    };

    const taskIds = new Set(tasks.map((t) => Number(t.id)));

    return links
      .filter((link) => taskIds.has(Number(link.source_id)) && taskIds.has(Number(link.target_id)))
      .map((link) => ({
        id: link.id,
        source: Number(link.source_id),
        target: Number(link.target_id),
        type: apiTypeToGanttType[link.dependency_type] || "0",
      }));
  }, [links, tasks]);

  const phaseOptions = useMemo(() => {
    return schedules.filter((s) => s.type === "phase" && !schedules.some((sg) => sg.type === "stage_gate" && sg.parent_id === s.id)).map((s) => ({ id: s.id, name: s.name }))
  }, [schedules]);

  const phaseOptionsActivity = useMemo(() => {
    return schedules.filter((s) => s.type === "phase").map((s) => ({ id: s.id, name: s.name }))
  }, [schedules]);

  const getPhaseOptionsForEdit = useMemo(() => {
    if (!editingScheduleItem) return [];
    return schedules.filter((s) => s.type === "phase" && !schedules.some((sg) => sg.type === "stage_gate" && sg.parent_id === s.id && sg.id !== editingScheduleItem.id)).map((s) => ({ id: s.id, name: s.name }))
  }, [schedules, editingScheduleItem]);

  const isStageGateCompleted = useMemo(() => {
    if (!editingScheduleItem) return false;
    if (editingScheduleItem.type === "stage_gate") {
      return editingScheduleItem.status === "completed";
    } else if (editingScheduleItem.type === "phase") {
      const stageGate = schedules.find((s) => s.type === "stage_gate" && s.parent_id === editingScheduleItem.id);
      return stageGate?.status === "completed";
    } else if (editingScheduleItem.type === "activity") {
      const stageGate = schedules.find((s) => s.type === "stage_gate" && s.parent_id === editingScheduleItem.parent_id);
      return stageGate?.status === "completed";
    }
    return false;
  }, [editingScheduleItem, schedules]);

  const areAllActivitiesCompleted = useMemo(() => {
    if (!editingScheduleItem || editingScheduleItem.type !== "phase") return false;

    const phaseActivities = schedules.filter(
      (s) => s.type === "activity" && s.parent_id === editingScheduleItem.id
    );

    if (phaseActivities.length === 0) return true;

    return phaseActivities.every((activity) => activity.status === "completed");
  }, [editingScheduleItem, schedules]);

  const hasLinkedStageGate = useMemo(() => {
    if (!editingScheduleItem || editingScheduleItem.type !== "phase") return false;

    return links.some((link) => {
      const sourceId = Number(link.source_id);
      const targetId = Number(link.target_id);
      const phaseId = editingScheduleItem.id;

      const isPhaseSourceStageGateTarget =
        sourceId === phaseId && schedules.some(s => s.id === targetId && s.type === "stage_gate");

      const isStageGateSourcePhaseTarget =
        targetId === phaseId && schedules.some(s => s.id === sourceId && s.type === "stage_gate");

      return isPhaseSourceStageGateTarget || isStageGateSourcePhaseTarget;
    });
  }, [editingScheduleItem, schedules, links]);

  const isLinkedStageGateCompleted = useMemo(() => {
    if (!editingScheduleItem || editingScheduleItem.type !== "phase" || !hasLinkedStageGate) return false;

    const linkedStageGate = schedules.find((s) => {
      if (s.type !== "stage_gate") return false;

      return links.some((link) => {
        const sourceId = Number(link.source_id);
        const targetId = Number(link.target_id);
        const phaseId = editingScheduleItem.id;

        const isPhaseSourceStageGateTarget =
          sourceId === phaseId && targetId === s.id;

        const isStageGateSourcePhaseTarget =
          targetId === phaseId && sourceId === s.id;

        return isPhaseSourceStageGateTarget || isStageGateSourcePhaseTarget;
      });
    });

    return linkedStageGate?.status === "completed";
  }, [editingScheduleItem, schedules, links, hasLinkedStageGate]);

  const isEstimateValidationStageGateCompleted = useMemo(() => {
    return schedules.find((s) => s.type === "stage_gate" && s.stage_gate_id === 2)?.status === "completed";
  }, [editingScheduleItem, schedules]);

  const isCommercialFocusActivity = useMemo(() => {
    if (!editingScheduleItem) return false;
    return activityFormData.category_key === "Commercial_Focus"
  }, [activityFormData]);

  const isCommercialFocusPhase = useMemo(() => {
    if (!editingScheduleItem) return false;
    return schedules.filter((s) => s.parent_id === editingScheduleItem.id && s.type === "activity").some((s) => s.category === "Commercial_Focus")
  }, [phaseFormData]);

  // ==================== DRAWER HANDLERS ====================
  const handleOpenDrawer = ({ riskId, type }: { riskId: string, type: string }) => {
    const params = new URLSearchParams(searchParams);
    if (riskId) {
      params.set("id", riskId);
    }
    params.set("type", type);
    setSearchParams(params, { replace: true });
  };

  const handleDrawerClose = useCallback(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("id");
      params.delete("type");
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  const handleTaskDblClick = useCallback((task: Task) => {
    if (task.type === "outage_window" || task.type === "outage_windows_group") {
      handleOpenDrawer({ riskId: task.id as string, type: "outageWindow" });
    } else {
      handleOpenDrawer({ riskId: task.id as string, type: task.type === "milestone" ? "stageGate" : task.type === "project" ? "phase" : "activity" });
    }
  }, [getScheduleById]);

  const handleAddTaskFromGantt = useCallback(() => {
    handleOpenDrawer({ riskId: "", type: "activity" });
  }, []);

  const handleMenuItemClick = useCallback((type: DrawerType) => {
    handleOpenDrawer({ riskId: "", type: type });
  }, []);

  // ==================== GANTT TASK HANDLERS ====================
  const handleTaskUpdate = useCallback(
    async (task: Task) => {
      try {
        const scheduleItem = getScheduleById(task.id as number);
        if (!scheduleItem || !task.start_date) return;

        const startDate = task.start_date;
        const endDate = calculateEndDate(startDate, task.duration || 0);
        const endDateForAPI = formatDateForAPI(endDate);

        if (scheduleItem.type === "phase") {
          const validation = validatePhaseDatesAgainstOutageWindow(scheduleItem, endDateForAPI);
          if (!validation.allowed) {
            ganttRef.current?.showMessage("error", validation.message || "This change is not allowed.");
            ganttRef.current?.revertTasks(tasks, ganttLinks);
            return;
          }
        }

        await updateSchedule(scheduleItem.id, {
          planned_start_date: formatDateForAPI(startDate),
          planned_end_date: endDateForAPI,
        });
        await refetch();
      } catch (error) {
        console.error("Failed to update task dates:", error);
      }
    },
    [getScheduleById, updateSchedule, refetch, validatePhaseDatesAgainstOutageWindow, tasks, ganttLinks]
  );

  const handleProgressUpdate = useCallback(
    async (task: Task) => {
      try {
        const scheduleItem = getScheduleById(task.id as number);
        if (!scheduleItem) return;

        const progressPercentage = Math.round((task.progress || 0) * 100);
        const newStatus = getStatusFromProgress(progressPercentage);

        if (scheduleItem.type === "phase" && newStatus === "completed") {
          const validation = validatePhaseCompletionBusinessRules(scheduleItem);
          if (!validation.allowed) {
            ganttRef.current?.showMessage("error", validation.message || "This phase cannot be marked Completed yet.");
            ganttRef.current?.revertTasks(tasks, ganttLinks);
            return;
          }
        }

        await updateSchedule(scheduleItem.id, {
          progress_percentage: progressPercentage.toString(),
          status: newStatus,
        });
        await refetch();
      } catch (error) {
        console.error("Failed to update task progress:", error);
      }
    },
    [getScheduleById, updateSchedule, refetch, validatePhaseCompletionBusinessRules, tasks, ganttLinks]
  );

  const handleTaskReorder = useCallback(
    async (items: { id: number; sequence_order: number }[]) => {
      try {
        const payload: UpdateSchedulingItemPayload[] = items.map((item) => ({
          id: item.id,
          sequence_order: item.sequence_order,
        }));
        await updateMultipleSchedules(payload);
      } catch (error) {
        console.error("Failed to reorder tasks:", error);
      }
    },
    [updateMultipleSchedules]
  );

  // ==================== DELETE HANDLERS ====================
  const handleDeleteRequest = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!editingScheduleItem) return;

    setDeleteConfirmOpen(false);
    try {
      await deleteSchedule(editingScheduleItem.id);
      handleDrawerClose();
    } catch (error) {
      console.error("Failed to delete schedule item:", error);
    }
  }, [editingScheduleItem, deleteSchedule, handleDrawerClose]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmOpen(false);
  }, []);

  // ==================== SAVE HANDLERS ====================
  const handleSavePhase = useCallback(
    async (data: PhaseFormData) => {
      try {
        if (editingScheduleItem) {
          await updateSchedule(editingScheduleItem.id, {
            name: data.phase,
            phase_type_id: data.phase_type_id ?? undefined,
            planned_start_date: data.plannedStartDate ? formatApiStartDate(data.plannedStartDate) ?? undefined : undefined,
            planned_end_date: data.plannedEndDate ? formatApiEndDate(data.plannedEndDate) ?? undefined : undefined,
            actual_end_date:  data.actualEndDate ? formatApiEndDate(data.actualEndDate) ?? undefined : undefined,
            progress_percentage: data.progress.toString(),
            status: (data.status === "Planned" ? "planned" : data.status === "On Hold" ? "on_hold" : data.status === "Completed" ? "completed" : "in_progress") as any,
            color: data.color,
            user_id: data.userId || null,
          });
        } else {
          await createSchedule({
            parent_id: null,
            type: "phase",
            name: data.phase,
            phase_type_id: data.phase_type_id,
            planned_start_date: data.plannedStartDate ? formatApiStartDate(data.plannedStartDate) ?? undefined : undefined,
            planned_end_date: data.plannedEndDate ? formatApiEndDate(data.plannedEndDate) ?? undefined : undefined,
            progress_percentage: data.progress.toString(),
            status: (data.status === "Planned" ? "planned" : data.status === "On Hold" ? "on_hold" : data.status === "Completed" ? "completed" : "in_progress") as any,
            color: data.color,
            user_id: data.userId || null,
            ...(data.status === "Completed" && {actual_end_date: formatApiEndDate(data.actualEndDate)})
          });
        }
        await refetch();
        removeCachestages();
        removeCacheProjects();
        handleDrawerClose();
      } catch (error) {
        console.error("Failed to save phase:", error);
      }
    },
    [editingScheduleItem, updateSchedule, createSchedule, handleDrawerClose, refetch]
  );

  const handleSaveActivity = useCallback(
    async (data: ActivityFormData) => {
      if(!data.plannedStartDate || !data.plannedEndDate) return;
      try {
        if (editingScheduleItem) {
          await updateSchedule(editingScheduleItem.id, {
            name: data.title,
            parent_id: data.phaseId || editingScheduleItem.parent_id,
            category: data.category_key || null,
            planned_start_date: formatApiStartDate(data.plannedStartDate) ?? undefined,
            planned_end_date: formatApiEndDate(data.plannedEndDate) ?? undefined,
            actual_end_date: data.actualEndDate ? formatApiEndDate(data.actualEndDate) : null,
            progress_percentage: data.progress.toString(),
            status: getStatusFromProgress(data.progress),
            user_id: data.userId || null,
            is_recurring: data.is_recurring,
            description: data.description,
            asset_id: data.asset_id,
            ...(data.is_recurring ? { frequency: data.frequency } : {}),
            ...(data.frequency === 'weekly' && data.is_recurring ? { recurring_day: data.recurring_day } : {}),
            ...(data.frequency === 'monthly' && data.is_recurring ? { monthly_pattern: data.monthly_pattern } : {})
          });
        } else {
          await createSchedule({
            parent_id: data.phaseId || null,
            type: "activity",
            name: data.title,
            category: data.category_key || null,
            planned_start_date: formatApiStartDate(data.plannedStartDate) ?? undefined,
            planned_end_date: formatApiEndDate(data.plannedEndDate) ?? undefined,
            progress_percentage: data.progress.toString(),
            status: getStatusFromProgress(data.progress),
            role_id: 6,
            user_id: data.userId || null,
            asset_id: data.asset_id,
            is_recurring: data.is_recurring,
            description: data.description,
            ...(data.progress === 100 && { actual_end_date: data.actualEndDate ? formatApiEndDate(data.actualEndDate) : null}),
            ...(data.is_recurring ? { frequency: data.frequency } : {}),
            ...(data.frequency === 'weekly' && data.is_recurring ? { recurring_day: data.recurring_day } : {}),
            ...(data.frequency === 'monthly' && data.is_recurring ? { monthly_pattern: data.monthly_pattern } : {})
          });
        }
        await refetch();
        removeCachePinned();
        removeCacheUnpinned();
        removeOperationsCache();
        removeOperationsListCache();
        handleDrawerClose();
      } catch (error) {
        console.error("Failed to save activity:", error);
      }
    },
    [editingScheduleItem, updateSchedule, createSchedule, handleDrawerClose, refetch]
  );

  const handleSaveStageGate = useCallback(
    async (data: StageGateFormData) => {   
      if(!data.plannedStartDate || !data.plannedEndDate) return;   
      try {
        if (editingScheduleItem) {
          await updateSchedule(editingScheduleItem.id, {
            name: data.title,
            parent_id: data.parentPhaseId || editingScheduleItem.parent_id,
            planned_start_date: formatApiStartDate(data.plannedStartDate) ?? undefined,
            planned_end_date: formatApiEndDate(data.plannedEndDate) ?? undefined,
            user_id: data.userId || null,
            description: data.description,
          });
        } else {
          await createSchedule({
            parent_id: data.parentPhaseId || null,
            type: "stage_gate",
            name: data.title,
            stage_gate_id: data.stage_gate_id,
            planned_start_date: formatApiStartDate(data.plannedStartDate) ?? undefined,
            planned_end_date: formatApiEndDate(data.plannedEndDate) ?? undefined,
            status: "planned",
            progress_percentage: "0",
            user_id: data.userId || null,
            description: data.description,
          });
        }
        await refetch();
        handleDrawerClose();
      } catch (error) {
        console.error("Failed to save stage gate:", error);
      }
    },
    [
      editingScheduleItem,
      updateSchedule,
      createSchedule,
      handleDrawerClose,
      refetch,
    ]
  );

  // ==================== LINK HANDLERS ====================
  const handleLinkAdd = useCallback(async (link: Link) => {
    try {
      const ganttTypeToApiType: Record<string, DependencyType> = {
        "0": "finish_to_start",
        "1": "start_to_start",
        "2": "finish_to_finish",
        "3": "start_to_finish",
      };
      await createLinkMutation.mutateAsync({
        source_id: Number(link.source),
        target_id: Number(link.target),
        dependency_type: ganttTypeToApiType[link.type] || "start_to_start",
      });
      await refetch();
    } catch (error) {
      console.error("Failed to create link:", error);
    }
  }, [createLinkMutation, refetch]);

  const handleLinkDelete = useCallback(
    async (linkId: number) => {
      try {
        await deleteLinkMutation.mutateAsync(linkId);
        await refetch();
      } catch (error) {
        console.error("Failed to delete link:", error);
      }
    },
    [deleteLinkMutation, refetch]
  );

  // ==================== UI STATE HANDLERS ====================
  const handleAddClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClickWrapper = (type: "phase" | "activity" | "stageGate" | "outageWindow") => {
    handleMenuItemClick(type);
    handleMenuClose();
  };

  const handleZoomChange = (
    event: React.MouseEvent<HTMLElement>,
    newZoom: string | null
  ) => {
    if (newZoom !== null) {
      setZoomLevel(newZoom);
      ganttRef.current?.setZoom(newZoom);
    }
  };

  const handleToggleCollapse = () => {
    if (isCollapsed) {
      ganttRef.current?.expandAll();
    } else {
      ganttRef.current?.collapseAll();
    }
    setIsCollapsed(!isCollapsed);
  };

  // ==================== UTILITY FUNCTIONS ====================
  const enableAll = <T extends Record<string, boolean>>(fields: T): T => {
    return Object.fromEntries(Object.keys(fields).map((k) => [k, false])) as T;
  }

  const disableAll = <T extends Record<string, boolean>>(fields: T): T => {
    return Object.fromEntries(Object.keys(fields).map((k) => [k, true])) as T;
  }

  // ==================== EFFECTS ====================
  useEffect(() => {
    setPhaseDisabledConfig(enableAll(PHASE_FIELDS));
    setActivityDisabledConfig(enableAll(ACTIVITY_FIELDS));
    setStageGateDisabledConfig(enableAll(STAGE_GATE_FIELDS));

    if (!editingScheduleItem?.id || !schedules) return;

    const isPhaseAdmin = isAllowedToOverridePhaseLock;
    const isActivityAdmin = isAllowedToOverrideActivityLock;
    const isStageGateAdmin = isAllowedToOverrideStageGateLock;

    /* ---- PHASE ---- */
    if (editingScheduleItem.type === "phase") {
      if (isPhaseAdmin) {
        setPhaseDisabledConfig(enableAll(PHASE_FIELDS));
        return;
      }

      const stageCompleted = schedules.some(
        (s) =>
          s.type === "stage_gate" &&
          s.parent_id === editingScheduleItem.id &&
          s.status === "completed"
      );

      if (stageCompleted) {
        setPhaseDisabledConfig(disableAll(PHASE_FIELDS));
        return;
      }

      if (
        (editingScheduleItem.user_id && editingScheduleItem.is_action_owner) ||
        (editingScheduleItem.user_id == null && isAllowedToEditPhase)
      ) {
        setPhaseDisabledConfig(enableAll(PHASE_FIELDS));
      } else {
        setPhaseDisabledConfig(disableAll(PHASE_FIELDS));
      }
    }

    /* ---- ACTIVITY ---- */
    if (editingScheduleItem.type === "activity") {
      if (isActivityAdmin) {
        setActivityDisabledConfig(enableAll(ACTIVITY_FIELDS));
        return;
      }

      const stageCompleted = schedules.some(
        (s) =>
          s.type === "stage_gate" &&
          s.parent_id === editingScheduleItem.parent_id &&
          s.status === "completed"
      );

      if (stageCompleted) {
        setActivityDisabledConfig(disableAll(ACTIVITY_FIELDS));
        return;
      }

      if (editingScheduleItem.user_id && editingScheduleItem.is_action_owner) {
        setActivityDisabledConfig({
          ...disableAll(ACTIVITY_FIELDS),
          category: false,
          actualEndDate: false,
          progress: false,
          asset_id: false,
          // Owner can also edit planned dates (stage-gate completion still locks everything above).
          plannedStartDate: false,
          plannedEndDate: false,
        });
        return;
      }

      if (editingScheduleItem.user_id == null && isAllowedToEditActivity) {
        setActivityDisabledConfig(enableAll(ACTIVITY_FIELDS));
        return;
      }

      setActivityDisabledConfig(
        editingScheduleItem.user_id
          ? disableAll(ACTIVITY_FIELDS)
          : enableAll(ACTIVITY_FIELDS)
      );
    }

    /* ---- STAGE GATE ---- */
    if (editingScheduleItem.type === "stage_gate") {
      if (isStageGateAdmin) {
        setStageGateDisabledConfig(enableAll(STAGE_GATE_FIELDS));
        return;
      }

      if (editingScheduleItem.status === "completed") {
        setStageGateDisabledConfig(disableAll(STAGE_GATE_FIELDS));
        return;
      }

      if (editingScheduleItem.user_id && editingScheduleItem.is_action_owner) {
        setStageGateDisabledConfig(disableAll(STAGE_GATE_FIELDS));
        return;
      }

      if (editingScheduleItem.user_id == null && isAllowedToEditStageGate) {
        setStageGateDisabledConfig(enableAll(STAGE_GATE_FIELDS));
      } else {
        setStageGateDisabledConfig(disableAll(STAGE_GATE_FIELDS));
      }
    }
  }, [
    editingScheduleItem,
    schedules,
    isAllowedToEditPhase,
    isAllowedToEditActivity,
    isAllowedToEditStageGate,
    isAllowedToOverridePhaseLock,
    isAllowedToOverrideActivityLock,
    isAllowedToOverrideStageGateLock,
  ]);

  // ==================== RETURN ====================
  // ==================== RETURN ====================
  return {
    // Query State
    isLoading,
    isError,
    error,
    refetch,

    // Mutations
    createSchedule,
    updateSchedule,
    updateMultipleSchedules,
    deleteSchedule,

    // Getters
    getScheduleById,
    getSchedulesByParentId,
    getSchedulesByType,

    // Mutation Loading States
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // Form & Editing State
    openDrawer: type as DrawerType,
    stageGateId: id ? parseInt(id) : null,
    editingScheduleItem,
    deleteConfirmOpen,

    // Form Data
    phaseFormData,
    activityFormData,
    stageGateFormData,

    //commercial_focus
    isCommercialFocusActivity,
    isCommercialFocusPhase,

    // Drawer Handlers
    handleDrawerClose,
    handleTaskDblClick,
    handleAddTaskFromGantt,

    // Task Update Handlers
    handleTaskUpdate,
    handleProgressUpdate,
    handleTaskReorder,
    canEditTaskViaDrag,

    // Delete Handlers
    handleDeleteRequest,
    handleDeleteConfirm,
    handleDeleteCancel,

    // Save Handlers
    handleSavePhase,
    handleSaveActivity,
    handleSaveStageGate,

    // Link Handlers
    handleLinkAdd,
    handleLinkDelete,

    // Gantt Data
    tasks,
    ganttLinks,

    // UI State - Menu
    anchorEl,
    handleAddClick,
    handleMenuClose,
    handleMenuItemClickWrapper,

    // UI State - Zoom
    zoomLevel,
    handleZoomChange,

    // UI State - Collapse
    isCollapsed,
    handleToggleCollapse,

    // Visibility Controls
    showCriticalPath,
    setShowCriticalPath,
    showSlack,
    setShowSlack,

    // Form Disabled Configs
    phaseDisabledConfig,
    activityDisabledConfig,
    stageGateDisabledConfig,

    // Phase Options
    phaseOptions,
    phaseOptionsActivity,
    getPhaseOptionsForEdit,

    // Stage Gate Completion Checks
    isStageGateCompleted,
    areAllActivitiesCompleted,
    hasLinkedStageGate,
    isLinkedStageGateCompleted,
    isEstimateValidationStageGateCompleted,

    // Form References
    ganttRef,
    phaseFormRef,
    activityFormRef,
    stageGateFormRef,

    // Outage Windows
    outageWindows,
  };
};